import {
  FIND_GROUP,
  useCurrentWorkspace,
  useDeleteMutation,
  useFindByQuery,
  useGroupRoutines,
  useUpdateMutation
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  ConfirmationOverlay,
  Icon,
  SlideAction,
  TextInput as TextInputCropCircle,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { useMatch, useNavigate } from 'react-router-dom';
import * as yup from 'yup';


// ── GraphQL ───────────────────────────────────────────────────────────────────

const UPDATE_GROUP = `
  mutation UpdateGroup($input: MutateGroupInput!) {
    updateGroup(input: $input) {
      name
      workspace { id }
    }
  }
`;

const DELETE_GROUP = `
  mutation DeleteGroup($input: MutateGroupInput!) {
    deleteGroup(input: $input) {
      id
    }
  }
`;

const formSchema = yup.object({
  name: yup.string().required('Group name is required'),
});

// ── Main panel content ─────────────────────────────────────────────────────────

type Props = {
  groupIds: string[];
  /** Called after the group is saved or deleted — collapses the selection panel. */
  onComplete: () => void;
};

/**
 * Right-column flyout content when one or more group rows are selected.
 *
 * Figma V6 design: inline name form + full-width "Delete Group" outline button,
 * with save triggered via the header "Set" button.
 */
export const GroupsSelectionPanelMain = ({
  groupIds,
  onComplete,
}: Props) => {
  const currentWorkspace = useCurrentWorkspace();

  const groupId = groupIds.length === 1 ? groupIds[0] : undefined;

  if (!groupId || !currentWorkspace) return null;

  return (
    <GroupEditFormContent
      groupId={groupId}
      workspaceId={currentWorkspace}
      onComplete={onComplete}
    />
  );
};

// ── Inline edit form (CropCircle ON) ──────────────────────────────────────────

type GroupEditFormContentProps = {
  groupId: string;
  workspaceId: { id: string };
  onComplete: () => void;
};

/**
 * Inline form rendered in the selection panel when CropCircle redesign is active.
 *
 * Layout matches the Figma V6 right-panel spec (node 9013:80655):
 *   - Content section (padding 16, gap 24): Name TextInput with label
 *   - Footer section (padding 16):         "Delete Group" rectangular outline button
 *
 * The panel title updates dynamically to the group name once data loads,
 * matching the Figma design which shows the group name in the toolbar.
 */
export const GroupEditFormContent = ({
  groupId,
  workspaceId,
  onComplete,
}: GroupEditFormContentProps) => {
  const { t } = useTranslation();
  const translationPath = 'groups.deleteGroup';

  const filter = useMemo(
    () => ({ id: { eq: groupId }, workspace: { eq: workspaceId?.id } }),
    [groupId, workspaceId]
  );

  const { data, isLoading } = useFindByQuery({
    modelName: 'group',
    query: FIND_GROUP,
    variables: { filter },
  });

  const [groupName, setGroupName] = useState('');
  // useSlideLayoutSafe returns null when rendered outside a SlideLayout context.
  const slideLayout = useSlideLayoutSafe();
  // Stable function reference extracted so the data-load effect below doesn't
  // re-fire whenever the context object identity changes.
  const setColumnTitle = slideLayout?.setColumnTitle;

  const { mutateAsync: updateGroup } = useUpdateMutation({
    modelName: 'group',
    query: UPDATE_GROUP,
  });

  const { mutateAsync: deleteGroup } = useDeleteMutation({
    modelName: 'group',
    query: DELETE_GROUP,
  });

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const DEBOUNCE_DELAY = 1000; // milliseconds

  // Defined before debouncedSaveGroup so it can be included in its dep array and
  // won't go stale when groupId or workspaceId change between keystrokes.
  const saveGroup = useCallback(async (values: { name: string }) => {
    await updateGroup({
      input: { id: groupId, name: values.name, workspace: { id: workspaceId.id } },
    });
  }, [updateGroup, groupId, workspaceId]);

  const debouncedSaveGroup = useCallback((values: { name: string }) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      handleSubmit(saveGroup)();
    }, DEBOUNCE_DELAY);
  }, [handleSubmit, saveGroup]);

  useEffect(() => {
    if (!data?.items?.length) return;
    const name = data.items[0].name as string;
    reset({ name });
    setGroupName(name);
    // Update the panel column title to the group name to match the Figma V6 design
    // (toolbar shows the group name instead of "N group(s) selected").
    setColumnTitle?.(name);
  }, [data, reset, setColumnTitle]);

  // Clear the imperative title on unmount so SlideLayout falls back to the
  // next panel's `column.title` (e.g., "New Group" when "+" replaces selection).
  useEffect(() => {
    return () => setColumnTitle?.(undefined);
  }, [setColumnTitle]);



  const hideDeleteOverlay = () => slideLayout?.setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    await deleteGroup({ input: { id: groupId, workspace: { id: workspaceId.id } } });
    hideDeleteOverlay();
    Toast.show({
      type: 'blackToast',
      text1: t(`${translationPath}.toastTitle`),
      text2: t(`${translationPath}.toastMessage`, { groupName }),
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
    onComplete();
  };

  const handleDeletePress = () => {
    if (slideLayout) {
      slideLayout.setColumnOverlay(
        <ConfirmationOverlay
          title={t('common.confirmDelete', { name: groupName })}
          subtitle={t('common.deleteWarning')}
          confirmLabel={t('common.delete')}
          onConfirm={handleDeleteConfirm}
          cancelLabel={t(`${translationPath}.cancel`)}
          onCancel={hideDeleteOverlay}
          testID="group-delete-confirmation"
        />
      );
    } else {
      // Fallback for contexts rendered outside a SlideLayout (e.g. Storybook, tests).
      console.log('No slideLayout')
    }
  };

  if (isLoading) return null;

  return (
    <View style={inlineStyles.content}>
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInputCropCircle
            label={t('groups.editGroup.nameLabel', { defaultValue: 'Name' })}
            placeholder={t('groups.editGroup.namePlaceholder', {
              defaultValue: 'Group Name',
            })}
            value={value}
            onBlur={onBlur}
            width="100%"
            hideBorder
            onChangeText={(text) => {
              onChange(text);
              debouncedSaveGroup({ name: text });
            }}
          />
        )}
      />

      <Button
        variant="rectangular"
        size="large"
        state="outline"
        onPress={handleDeletePress}
        style={inlineStyles.deleteButton}
        accessibilityLabel={t(`${translationPath}.deleteGroup`)}
      >
        {t(`${translationPath}.deleteGroup`)}
      </Button>
    </View>
  );
};

// ── Header component ───────────────────────────────────────────────────────────

type HeaderProps = {
  groupId: string;
};

/**
 * Right-column header actions (CropCircle ON only).
 *
 * Renders in the `rightActions` slot of the PanelHeader:
 *   - Alert icon — navigates to the group's alert list 
 */
export const GroupSelectionPanelHeader = ({ groupId }: HeaderProps) => {
  const navigate = useNavigate();
  const { routinesCount, isLoading } = useGroupRoutines(groupId);
  const hasAlerts = routinesCount === 0;
  const isAlertsList = useMatch('/groups/:groupId/alerts');
  const slideLayout = useSlideLayoutSafe();

  return (
    <Pressable
      onPress={async () => {
        if (isLoading) {
          return;
        }

        if (!isAlertsList) {
          await slideLayout?.onSlideChange(SlideAction.NEXT);
          
          navigate(
            hasAlerts
               ? `/groups/${groupId}/alerts/new`
               : `/groups/${groupId}/alerts`
          );
        }
      }}
      style={[styles.alertIconButton]}
      accessibilityLabel="View Alerts"
      testID="group-selection-alerts-button"
    >
      <Icon
        name="Alert"
        size={24}
        color={Colors.role.textPrimary}
      />
    </Pressable>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  // Header: Alert icon
  alertIconButton: {
    padding: tokens.spacing.xs,
  },
});

// Styles for the CropCircle ON inline form layout (matches Figma node 9013:80655).
const inlineStyles = StyleSheet.create({
  // Unified content section: pad=16, gap=24 (holds name input + delete button)
  content: {
    padding: tokens.spacing.lg,
  },
  // Full-width outline delete button (height:48, radius:2 handled by Button component)
  deleteButton: {
    marginTop: tokens.spacing['2xl'],
    width: '100%',
  },
});
